import { useEffect, useMemo, useState } from "react";
import Breadcrumb from "../../../DesignSystem/Breadcrumb/Breadcrumb";
import Sidebar from "../../../DesignSystem/Sidebar";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import ButtonSecondarySmall from "../../../DesignSystem/Buttons/ButtonSecondarySmall";
import {
  subscribeAnnouncements,
  updateAnnouncementStatus,
  syncLiveAnnouncements,
} from "../utils/announcementService";
import {
  getComputedAnnouncementStatus,
  canEditAnnouncement,
} from "../utils/announcementStatusUtils";
import dayjs from "dayjs";
import { useServerSyncedTime } from "../../../../utils/useServerSyncedTime";

import "../../../../css/Marketing/Announcements/AnnouncementsPage.css";
import Pagination from "../../../DesignSystem/Pagination/Pagination";

const getStatusLabel = (announcement, currentTimestamp) => {
  const status = getComputedAnnouncementStatus(announcement, currentTimestamp);

  if (status === "draft") return "Draft";
  if (status === "scheduled") return "Scheduled";
  if (status === "archived") return "Archived";
  return "Active";
};

const AnnouncementsPage = () => {
  const navigate = useNavigate();
  const { effectiveTimestamp } = useServerSyncedTime();

  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [showScheduled, setShowScheduled] = useState(false);
  const [showActive, setShowActive] = useState(false);
  const [showPast, setShowPast] = useState(false);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [showDrafts, setShowDrafts] = useState(false);
  const itemsPerPage = 20;
  // useEffect(() => {
  //   const loadAnnouncements = async () => {
  //     try {
  //       setLoading(true);
  //       const data = await getAnnouncements();
  //       setAnnouncements(data);
  //     } catch (error) {
  //       console.error("Failed to load announcements:", error);
  //     } finally {
  //       setLoading(false);
  //     }
  //   };

  //   loadAnnouncements();
  // }, []);

  const reloadAnnouncements = async (latestAnnouncements) => {
    try {
      setLoading(true);

      const syncedData = await syncLiveAnnouncements(latestAnnouncements);

      setAnnouncements(syncedData);
    } catch (error) {
      // console.error("Failed to load announcements:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);

    const unsubscribe = subscribeAnnouncements(
      async (data) => {
        await reloadAnnouncements(data);
      },
      () => {
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, []);
  const handleArchiveAnnouncement = async (announcementId) => {
    try {
      setOpenMenuId(null);
      await updateAnnouncementStatus(announcementId, "archived");
    } catch (error) {
      // console.error("Failed to archive announcement:", error);
    }
  };

  const handleUnarchiveAnnouncement = async (announcement) => {
    try {
      setOpenMenuId(null);

      const nextStatus = announcement.scheduleAnnouncement
        ? getComputedAnnouncementStatus(
            {
              ...announcement,
              status: "scheduled",
            },
            effectiveTimestamp,
          )
        : "active";

      await updateAnnouncementStatus(
        announcement.id,
        nextStatus === "scheduled" ? "scheduled" : "active",
      );
    } catch (error) {
      // console.error("Failed to unarchive announcement:", error);
    }
  };

  useEffect(() => {
    reloadAnnouncements();
  }, []);

  const filteredAnnouncements = useMemo(() => {
    return announcements.filter((announcement) => {
      const status = getStatusLabel(
        announcement,
        effectiveTimestamp,
      ).toLowerCase();

      if (showScheduled && status !== "scheduled") return false;
      if (showActive && status !== "active") return false;
      if (showPast && status !== "archived") return false;
      if (showDrafts && status !== "draft") return false;

      const query = search.toLowerCase().trim();

      if (!query) return true;

      return (
        announcement.contentTitle?.toLowerCase().includes(query) ||
        announcement.contentType?.toLowerCase().includes(query)
      );
    });
  }, [
    announcements,
    search,
    showScheduled,
    showActive,
    showPast,
    showDrafts,
    effectiveTimestamp,
  ]);

  const totalPages = Math.ceil(filteredAnnouncements.length / itemsPerPage);

  const paginatedAnnouncements = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;

    return filteredAnnouncements.slice(startIndex, endIndex);
  }, [filteredAnnouncements, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, showScheduled, showActive, showPast, showDrafts]);

  return (
    <div className="Main-Page">
      <Helmet>
        <title>Announcements</title>
        <meta
          name="description"
          content="Create and manage company updates seen on adviser portal"
        />
      </Helmet>

      <Sidebar />

      <main className="Main-Container AnnouncementsPage">
        <Breadcrumb
          items={[
            { label: "Marketing", to: "/Department/Marketing" },
            { label: "Announcements" },
          ]}
        />

        <div className="fp-admin-head">
          <h2 className="FP_H3">Announcements</h2>

          <ButtonSecondarySmall
            label="New Announcement"
            onClick={() => navigate("/department/marketing/announcements/new")}
          />
        </div>

        <section className="announcements-dashboard">
          <div className="announcements-filters">
            <label className="announcements-filter-checkbox FP_Content">
              <input
                type="checkbox"
                checked={showScheduled}
                onChange={(e) => setShowScheduled(e.target.checked)}
              />
              Scheduled Announcements
            </label>

            <label className="announcements-filter-checkbox FP_Content">
              <input
                type="checkbox"
                checked={showActive}
                onChange={(e) => setShowActive(e.target.checked)}
              />
              Active Announcements
            </label>

            <label className="announcements-filter-checkbox FP_Content">
              <input
                type="checkbox"
                checked={showPast}
                onChange={(e) => setShowPast(e.target.checked)}
              />
              Past Announcements
            </label>
            <label className="announcements-filter-checkbox FP_Content">
              <input
                type="checkbox"
                checked={showDrafts}
                onChange={(e) => setShowDrafts(e.target.checked)}
              />
              Drafts
            </label>

            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Content Type/Content Title"
              className="FP_Content fp-form-input announcements-search"
            />
          </div>

          {loading ? (
            <div className="announcements-empty FP_Content">
              Loading announcements...
            </div>
          ) : filteredAnnouncements.length === 0 ? (
            <div className="announcements-empty FP_Content">
              No announcements found.
            </div>
          ) : (
            <div className="announcements-list">
              {paginatedAnnouncements.map((announcement) => {
                const statusLabel = getStatusLabel(
                  announcement,
                  effectiveTimestamp,
                );

                return (
                  <article
                    key={announcement.id}
                    className="announcement-card"
                    onClick={() =>
                      navigate(
                        `/department/marketing/announcements/${announcement.id}`,
                      )
                    }
                  >
                    <div className="announcement-card-image">
                      {announcement.headerImageFiles?.[0]?.url ? (
                        <img
                          src={announcement.headerImageFiles[0].url}
                          alt={announcement.contentTitle || "Announcement"}
                          className="announcement-card-image-img"
                        />
                      ) : (
                        <div className="announcement-card-image-placeholder">
                          {announcement.headerImageFiles?.[0]?.name
                            ? announcement.headerImageFiles[0].name
                                .charAt(0)
                                .toUpperCase()
                            : "A"}
                        </div>
                      )}
                    </div>

                    <div className="announcement-card-content">
                      <div className="FP_Help_Text uppercase">
                        {announcement.contentType || "Announcement"}
                      </div>

                      <h3 className="FP_Body_Semibold black">
                        {announcement.contentTitle || "Untitled Announcement"}
                      </h3>

                      <div className="announcement-card-meta">
                        <div className="announcement-card-stats">
                          <span className=" FP_Help_Text">Created By</span>
                          <span className="FP_Help_Text black">
                            {announcement.createdBy || "-"}
                          </span>
                        </div>
                        <div className="announcement-card-stats">
                          <span className=" FP_Help_Text">Created On</span>
                          <span className="FP_Help_Text black">
                            {(() => {
                              if (!announcement.createdAt) return "-";
                              const d = announcement.createdAt?.toDate
                                ? dayjs(announcement.createdAt.toDate())
                                : dayjs(announcement.createdAt);
                              return d.isValid()
                                ? d.format("DD MMM YYYY")
                                : "-";
                            })()}
                          </span>
                        </div>
                        <div className="announcement-card-stats">
                          <span className=" FP_Help_Text">Announced</span>
                          <span className="FP_Help_Text black">
                            {announcement.postedDate
                              ? dayjs(announcement.postedDate).format(
                                  "DD MMM YYYY",
                                )
                              : announcement.scheduleDate
                                ? dayjs(announcement.scheduleDate).format(
                                    "DD MMM YYYY",
                                  )
                                : announcement.createdAt?.toDate
                                  ? dayjs(
                                      announcement.createdAt.toDate(),
                                    ).format("DD MMM YYYY")
                                  : "-"}
                          </span>
                        </div>
                        <div className="announcement-card-stats">
                          <span className=" FP_Help_Text">Until</span>
                          <span className="FP_Help_Text black">
                            {announcement.keepUntilDate || "-"}
                          </span>
                        </div>
                      </div>
                      <div className="announcement-card-meta">
                        <div className="announcement-card-stats">
                          <span className="FP_Help_Text black">
                            Number of Groups
                          </span>
                          <span className="FP_Subtitle_Semibold black">
                            {announcement.announcementFor?.length || 0}
                          </span>
                        </div>
                        <div className="announcement-card-stats">
                          <span className="FP_Help_Text black">
                            Number of Clicks
                          </span>
                          <span className="FP_Subtitle_Semibold black">
                            {announcement.clickCount || 0}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div
                      className={`FP_Content_Semibold announcement-status announcement-status--${statusLabel.toLowerCase()}`}
                    >
                      {statusLabel}
                    </div>

                    <div className="announcement-menu-wrap">
                      <button
                        type="button"
                        className="announcement-menu-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenMenuId((currentId) =>
                            currentId === announcement.id
                              ? null
                              : announcement.id,
                          );
                        }}
                        aria-label="Announcement options"
                      >
                        ⋮
                      </button>

                      {openMenuId === announcement.id && (
                        <div
                          className="announcement-menu-dropdown"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            className="FP_Content"
                            type="button"
                            onClick={() => {
                              setOpenMenuId(null);
                              navigate(
                                `/department/marketing/announcements/${announcement.id}`,
                              );
                            }}
                          >
                            View Details
                          </button>

                          {statusLabel.toLowerCase() === "archived" ? (
                            <button
                              type="button"
                              className="FP_Content"
                              onClick={() =>
                                handleUnarchiveAnnouncement(announcement)
                              }
                            >
                              Unarchive Announcement
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="FP_Content"
                              onClick={() =>
                                handleArchiveAnnouncement(announcement.id)
                              }
                            >
                              Archive Announcement
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>
        {totalPages > 1 && (
          <div className="announcements-pagination">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              maxVisiblePages={5}
            />
          </div>
        )}
      </main>
    </div>
  );
};

export default AnnouncementsPage;
